import _ from 'lodash';
import { connect } from 'react-redux';
import { mapProps, compose } from 'recompose';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import WithPropsOnce from './WithPropsOnce';
import { formulaIterate, isBlock } from '../utils/formula';

import { getExpression } from '../utils/utils';
import { getAllMethods, getGroupMethods, getMethod } from '../utils/methods';
import getFunctionSelectOption from '../utils/getFunctionSelectOption';
import type { Dispatch } from 'redux';
import type { RootState } from '../redux/store';
import type { FormulaNode, FunctionFormula, LogMap, UiFormulaNode } from '../typeDefs';

interface OwnProps {
    path: string;
    depth?: number;
    dispatch: Dispatch;
    dataSelector?: (state: RootState, props: OwnProps) => UiFormulaNode;
}

/** A single entry in an operation-selector menu. */
type OperationOption = Array<string | undefined | { title?: string }>;

// used for checking availableFunction equality
const createIdEqualitySelector = createSelectorCreator(
    defaultMemoize,
    (a: FunctionFormula[], b: FunctionFormula[]) => {
        const hash = (arr: FunctionFormula[]) => _.map(arr, item => item.id + '-' + item.name).sort().join(';');
        return hash(a) === hash(b);
    },
);

const getAvailableFunctions = createIdEqualitySelector(
    (state: RootState) => state.formula.data.functions,
    (arr: FunctionFormula[]) => _.keyBy(arr, 'id'),
);

const getAvailableVariables = (state: RootState): Record<string, string> => {
    const map: Record<string, string> = {};
    formulaIterate(state.formula.data, ({ $op, $value, $args = [] }) => {
        if (
            $op === 'fn' &&
            [
                'varSet',
                'varRefSet',
                'stringRegExp',
                'arrayFilter',
                'arrayFindStringAtIndex',
                'stringContainsWord',
            ].includes($value)
        ) {
            if ($value === 'varSet') map[$args[0]] = 'number';
            else if ($value === 'arrayFindStringAtIndex') map[$args[0]] = 'string';
            else map[$args[0]] = 'list';
        }
    });
    return map;
};

const getHasClipboard = (state: RootState): boolean => !!_.get(state, 'formula.clipboard');

const getLog = (state: RootState): LogMap | null => state.formula.log;

const getStruct        = (state: RootState, props: OwnProps): FormulaNode => _.get(state, 'formula.data.' + props.path) as FormulaNode;
const getCollapseDepth = (state: RootState): number => _.get(state, 'formula.uiOptions.collapseDepth', 6);
const getDispatch      = (_state: RootState, props: OwnProps): Dispatch => props.dispatch;
const getPath          = (_state: RootState, props: OwnProps): string => props.path;
const getDepth         = (_state: RootState, props: OwnProps): number => props.depth || 0;

export default compose(
    // We @connect() twice since the 2nd connect needs access to dispatch
    connect(),
    WithPropsOnce(() => {
        const dataSelector = createSelector(
            [
                getPath,
                getStruct,
                getLog,
                getDispatch,
                getAvailableFunctions,
                getAvailableVariables,
                getHasClipboard,
                getDepth,
                getCollapseDepth,
            ],
            (
                path,
                struct,
                log,
                dispatch,
                availableFunctions,
                availableVariables,
                hasClipboard,
                depth,
                collapseDepth,
            ) =>
                createData({
                    dispatch,
                    struct,
                    log,
                    path,
                    availableFunctions,
                    availableVariables,
                    hasClipboard,
                    depth,
                    collapseDepth,
                }),
        );

        return {
            dataSelector,
        };
    }),
    connect((state: RootState, props: OwnProps) => ({
        data: props.dataSelector!(state, props),
    })),
    mapProps(({ dataSelector, ...props }: OwnProps) => props),
);

const concatPath = (a: string | null | undefined, b: string | null | undefined): string =>
    a == null || a === '' ? b || '' :
    b == null || b === '' ? a || '' :
    a + '.' + b;

interface CreateDataArgs {
    dispatch: Dispatch;
    struct: FormulaNode;
    log: LogMap | null;
    path: string;
    availableFunctions: Record<string, FunctionFormula>;
    availableVariables: Record<string, string>;
    hasClipboard: boolean;
    depth: number;
    collapseDepth: number;
}

const ID_SYMBOL = Symbol('id');
let uuid = 0;
const createData = ({
    dispatch,
    struct,
    log,
    path,
    availableFunctions,
    availableVariables,
    hasClipboard,
    depth,
    collapseDepth,
}: CreateDataArgs): UiFormulaNode => {
    const { Actions } = require('../redux/Formula') as typeof import('../redux/Formula');

    const hasLog = !!log;

    if (!struct) console.warn(`NO STRUCT: "${path}"`);

    const structSym = struct as unknown as Record<symbol, string>;
    const ID = structSym[ID_SYMBOL] || 'id-' + (++uuid);
    structSym[ID_SYMBOL] = ID;

    const isCall = struct.$op === 'call';

    const getResult = (logMap: LogMap, parentPath?: string | null): unknown =>
        parentPath == null ? null :
        _.get(logMap[concatPath(path.split('.')[0], parentPath)], ['result']);

    return _.defaults({}, struct, {
        dispatch,
        ID,
        plain: struct,
        path,
        availableFunctions,
        availableVariables,
        hasClipboard,
        depth,
        collapseDepth,
        defaultCollapsed: depth >= collapseDepth,

        log: Object.assign(
            {
                hasLog: hasLog && !path.includes('functions'),
                parentResult: !hasLog ? null : getResult(log!, _.get(struct, ['state', 'parentPath'])),
            },
            !hasLog ? null : log![path],
            !hasLog || !isCall ? null : {
                artifacts: _.get(log![`${path}_nested`], 'artifacts', null),
                nested: log![path + '_nested'],
            },
        ),

        operationOptions: getOperations(
            availableFunctions,
            _.get(struct, 'state.otherOperations', []) as OperationOption[],
            { hasClipboard },
        ),

        getChildPath: (subPath: string) => concatPath(path, subPath),

        getChildLogResult: (subPath: string) =>
            !hasLog ? null :
            _.get(
                log![concatPath(path, subPath)] || log![concatPath(path, subPath) + '_nested'],
                'result',
            ),

        change: (subPath: string | Record<string, unknown>, value?: unknown) => {
            if (subPath && typeof subPath === 'object') return dispatch(Actions.setInMultiple(path, subPath));
            return dispatch(Actions.setIn(concatPath(path, subPath), value));
        },

        remove: () => dispatch(Actions.remove(path)),

        replace: (key: string, args: unknown[] = []) => {
            if (typeof key !== 'string') throw new Error(`Invalid key: '${key}' (${typeof key})`);

            // If swapping from an $and to an $or or vice-versa, keep everything in place
            if (isBlock(key) && isBlock(struct.$op)) {
                if (key === struct.$op) return;
                return dispatch(Actions.setIn(concatPath(path, '$op'), key));
            }

            // If replacing a non-block with a block. wrap it instead
            if (!isBlock(struct.$op)) {
                if (isBlock(key)) return dispatch(Actions.setIn(path, { $op: key, $value: [struct] }));
                if (key === 'if') {
                    return dispatch(Actions.setIn(path, {
                        $op: 'if',
                        $value: struct,
                        $then: { $op: 'fn', $value: 'constant', $args: ['false'] },
                        $else: { $op: 'fn', $value: 'constant', $args: ['false'] },
                    }));
                }
            }

            return dispatch(Actions.setIn(path, getExpression(key)!.getStub(args)));
        },

        unwrap: () => !struct.state?.actions?.unwrap ? null :
            dispatch(Actions.setIn(path, struct.$value[0])),

        setState: (state: unknown) => dispatch(Actions.setState(path, state)),

        makeFunction: () => struct.$op === 'call' ? null : dispatch(Actions.makeFunction(path)),

        clipboardAdd: () => dispatch(Actions.clipboardAdd(struct)),

        clipboardPaste: (subPath: string) => dispatch(Actions.clipboardPaste(concatPath(path, subPath))),
    }) as unknown as UiFormulaNode;
};

const getOperations = (
    functions: Record<string, FunctionFormula>,
    other: OperationOption[] = [],
    { hasClipboard }: { hasClipboard: boolean },
): OperationOption[] => [
    ...(!hasClipboard ? [] : [
        ['clipboard', 'CLIPBOARD', 'Operations'],
    ]),

    ..._.map(getGroupMethods('Basic'), ({ key, label, group }) => [
        key,
        label,
        group,
        { title: getMethod(key)?.description },
    ]),

    ..._.map(getGroupMethods('Variable-Related'), ({ key, label, group }) => [
        `fn:${key}`,
        label,
        group,
        { title: getMethod(key)?.description },
    ]),

    ..._.map(getGroupMethods('Operators'), ({ key, label, group }) => [
        `fn:${key}`,
        label,
        group,
        { title: getMethod(key)?.description },
    ]),

    ..._.map(getGroupMethods('Read/Write'), ({ key, label, group }) => [
        key === 'ref' ? key : `fn:${key}`,
        label,
        group,
        { title: getMethod(key)?.description },
    ]),

    ..._.sortBy(
        _.map(functions, ({ id, name }) => {
            const [n, g] = getFunctionSelectOption(name);
            return ['call:' + id, n, 'Custom' + (g ? ': ' + g : '')];
        }),
        2,
    ),

    ...(other || []),

    ..._.map(getGroupMethods('Deprecated'), ({ key, label, group }) => [
        key === 'schema' ? key : `fn:${key}`,
        label,
        group,
        { title: getMethod(key)?.description },
    ]),

    ..._.map(_.filter(getAllMethods(), m => !m.group), ({ key, label }) => [
        `fn:${key}`,
        label,
        'Deprecated',
        { title: getMethod(key)?.description },
    ]),
];
